// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useToast } from "@crm-src/hooks/use-toast"
import { ContactPortalAccessButton, type ContactPortalState } from "@crm-src/components/support-portal/ContactPortalAccessButton"
import { fetchCustomerSyncDetail } from "@crm-src/lib/support-sync-api"
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  Building2,
  User,
  Clock,
  Users,
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  ChevronDown,
} from "lucide-react"

import { customerContactsApi } from "@crm-src/lib/customer-contacts-api"
import type { CustomerContact, CustomerContactCreateData, CustomerContactUpdateData } from "@crm-src/lib/customer-contacts-api"

interface ContactInformationData {
  full_name?: string
  job_title?: string
  email?: string
  email2?: string
  phone?: string
  phone2?: string
  mobile?: string
  website?: string
  timezone?: string
  address?: string
  city?: string
  state?: string
  country?: string
  postal_code?: string
  postal?: string // For prospects
  customer_contacts?: CustomerContact[]
}

interface EditableContactInformationPanelProps {
  data: ContactInformationData
  customerId: string
  className?: string
  onContactsUpdated?: (contacts: CustomerContact[]) => void
}

// Shared icon + label + value row used throughout the contact detail views.
function InfoRow({ icon, label, value, alignTop }: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  alignTop?: boolean
}) {
  return (
    <Box sx={{ display: "flex", alignItems: alignTop ? "flex-start" : "center", gap: 1.5 }}>
      <Box sx={{ display: "flex", color: "text.disabled", flexShrink: 0, ...(alignTop ? { mt: 0.5 } : {}) }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{label}</Typography>
        {value}
      </Box>
    </Box>
  )
}

export function EditableContactInformationPanel({
  data,
  customerId,
  className = "",
  onContactsUpdated
}: EditableContactInformationPanelProps) {
  const { toast } = useToast()

  // Use customer_contacts if available, otherwise fall back to legacy single contact
  const [contacts, setContacts] = useState<CustomerContact[]>(
    data.customer_contacts && data.customer_contacts.length > 0
      ? data.customer_contacts
      : []
  )

  // Modal states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editingContact, setEditingContact] = useState<CustomerContact | null>(null)
  const [contactToDelete, setContactToDelete] = useState<CustomerContact | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  // Per-contact actions menu anchor (keyed by contact id) — replaces the
  // shadcn DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; contactId: number } | null>(null)

  // Which contact cards are expanded, keyed by contact id. Cards start
  // COLLAPSED: a customer with a handful of contacts otherwise pushes the rest
  // of the detail page far below the fold, and the header (avatar + name) is
  // enough to find the one you want.
  const [expandedContactIds, setExpandedContactIds] = useState<Set<number>>(new Set())

  const toggleContactExpanded = (contactId: number) => {
    setExpandedContactIds((prev) => {
      const next = new Set(prev)
      if (next.has(contactId)) {
        next.delete(contactId)
      } else {
        next.add(contactId)
      }
      return next
    })
  }

  // Per-contact support-portal access state, keyed by contact id.
  // Loads silently — fails closed (buttons hidden) if the user lacks
  // the admin scope on /api/v1/admin/support_sync/customers/:id.
  const [portalStates, setPortalStates] = useState<Record<number, ContactPortalState>>({})

  const reloadPortalStates = useCallback(async () => {
    try {
      const detail = await fetchCustomerSyncDetail(customerId)
      const map: Record<number, ContactPortalState> = {}
      for (const c of detail.contacts) {
        map[c.id] = c.portal as ContactPortalState
      }
      setPortalStates(map)
    } catch {
      // 403 (non-admin) or 404 (customer not yet mirrored) — hide controls
      setPortalStates({})
    }
  }, [customerId])

  useEffect(() => { reloadPortalStates() }, [reloadPortalStates])
  const [isDeleting, setIsDeleting] = useState(false)

  // Form state
  const [formData, setFormData] = useState<CustomerContactCreateData>({
    first_name: "",
    last_name: "",
    email: "",
    email2: "",
    phone: "",
    phone2: "",
    mobile: "",
    company_name: "",
    job_title: "",
    website: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    timezone: "",
  })

  const resetForm = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      email2: "",
      phone: "",
      mobile: "",
      company_name: "",
      job_title: "",
      website: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      timezone: "",
    })
  }

  // Handle add contact
  const handleAddContact = () => {
    resetForm()
    setIsAddDialogOpen(true)
  }

  // Handle edit contact
  const handleEditContact = (contact: CustomerContact) => {
    setEditingContact(contact)
    setFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      email: contact.email || "",
      email2: contact.email2 || "",
      phone: contact.phone || "",
      phone2: contact.phone2 || "",
      mobile: contact.mobile || "",
      company_name: contact.company_name || "",
      job_title: contact.job_title || "",
      website: contact.website || "",
      address: contact.address || "",
      city: contact.city || "",
      state: contact.state || "",
      country: contact.country || "",
      postal_code: contact.postal_code || "",
      timezone: contact.timezone || "",
    })
    setIsEditDialogOpen(true)
  }

  // Handle delete contact
  const handleDeleteContact = (contact: CustomerContact) => {
    setContactToDelete(contact)
    setIsDeleteDialogOpen(true)
  }

  // Save contact (create or update)
  const handleSaveContact = async () => {
    setIsSaving(true)
    try {
      let updatedContact: CustomerContact

      if (editingContact) {
        // Update existing contact
        const response = await customerContactsApi.update(customerId, editingContact.id.toString(), formData)
        updatedContact = response.contact

        // Update local state
        const updatedContacts = contacts.map(contact =>
          contact.id === updatedContact.id ? updatedContact : contact
        )
        setContacts(updatedContacts)
        onContactsUpdated?.(updatedContacts)

        toast({
          title: "Success",
          description: "Contact updated successfully",
        })
      } else {
        // Create new contact
        const response = await customerContactsApi.create(customerId, formData)
        updatedContact = response.contact

        // Add to local state
        const updatedContacts = [...contacts, updatedContact]
        setContacts(updatedContacts)
        onContactsUpdated?.(updatedContacts)

        toast({
          title: "Success",
          description: "Contact created successfully",
        })
      }

      // Close dialog and reset
      setIsAddDialogOpen(false)
      setIsEditDialogOpen(false)
      setEditingContact(null)
      resetForm()

    } catch (error: any) {
      console.error('Error saving contact:', error)
      const errorMessages = error.response?.data?.errors || [error.response?.data?.error || "Failed to save contact"]
      toast({
        title: "Error",
        description: Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages,
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Confirm delete contact
  const confirmDeleteContact = async () => {
    if (!contactToDelete) return

    setIsDeleting(true)
    try {
      await customerContactsApi.delete(customerId, contactToDelete.id.toString())

      // Remove from local state
      const updatedContacts = contacts.filter(contact => contact.id !== contactToDelete.id)
      setContacts(updatedContacts)
      onContactsUpdated?.(updatedContacts)

      toast({
        title: "Success",
        description: "Contact deleted successfully",
      })

      setIsDeleteDialogOpen(false)
      setContactToDelete(null)

    } catch (error: any) {
      console.error('Error deleting contact:', error)
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Legacy single contact fallback
  if (contacts.length === 0 && (data.full_name || data.email)) {
    const postalCode = data.postal_code || data.postal

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} {...(className ? { className } : {})}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography component="h4" sx={{ fontWeight: 500 }}>Contact Information</Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<Plus size={16} />}
            onClick={handleAddContact}
          >
            Add Contact
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Legacy single contact display */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <InfoRow
              icon={<User size={16} />}
              label="Primary Contact"
              value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.full_name || 'N/A'}</Typography>}
            />
            {data.job_title && (
              <InfoRow
                icon={<Building2 size={16} />}
                label="Job Title"
                value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.job_title}</Typography>}
              />
            )}
          </Box>

          {/* Email Information */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <InfoRow
              icon={<Mail size={16} />}
              label="Primary Email"
              value={
                <Typography sx={{ fontSize: 14, color: data.email ? "text.secondary" : "text.disabled", ...(data.email ? {} : { fontStyle: "italic" }) }}>
                  {data.email || 'Email not specified'}
                </Typography>
              }
            />
            {data.email2 && (
              <InfoRow
                icon={<Mail size={16} />}
                label="Email 2"
                value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.email2}</Typography>}
              />
            )}
          </Box>

          {/* Phone Information */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.phone && data.phone.trim() ? (
              <InfoRow icon={<Phone size={16} />} label="Phone" value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.phone}</Typography>} />
            ) : null}
            {data.phone2 && data.phone2.trim() ? (
              <InfoRow icon={<Phone size={16} />} label="Phone 2" value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.phone2}</Typography>} />
            ) : null}
            {data.mobile && data.mobile.trim() ? (
              <InfoRow icon={<Phone size={16} />} label="Mobile" value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.mobile}</Typography>} />
            ) : null}
            {!data.phone?.trim() && !data.phone2?.trim() && !data.mobile?.trim() && (
              <InfoRow
                icon={<Phone size={16} />}
                label="Phone"
                value={<Typography sx={{ fontSize: 14, color: "text.disabled", fontStyle: "italic" }}>Phone not specified</Typography>}
              />
            )}
          </Box>

          {/* Web & Location */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {data.website && (
              <InfoRow icon={<Globe size={16} />} label="Website" value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.website}</Typography>} />
            )}
            {data.timezone && (
              <InfoRow icon={<Clock size={16} />} label="Timezone" value={<Typography sx={{ fontSize: 14, color: "text.secondary" }}>{data.timezone}</Typography>} />
            )}
          </Box>

          {/* Address Information */}
          {(data.address?.trim() || data.city?.trim() || data.state?.trim() || data.country?.trim()) ? (
            <InfoRow
              icon={<MapPin size={16} />}
              label="Address"
              alignTop
              value={
                <Box sx={{ fontSize: 14, color: "text.secondary", display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {data.address && <Typography sx={{ fontSize: 14 }}>{data.address}</Typography>}
                  {(data.city || data.state || postalCode) && (
                    <Typography sx={{ fontSize: 14 }}>
                      {[data.city, data.state].filter(v => v && v.trim()).join(', ')}
                      {postalCode && postalCode.trim() && ` ${postalCode.trim()}`}
                    </Typography>
                  )}
                  {data.country && <Typography sx={{ fontSize: 14 }}>{data.country}</Typography>}
                </Box>
              }
            />
          ) : (
            <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, fontSize: 14, color: "text.secondary" }}>
              Address not provided
            </Box>
          )}
        </Box>

        {/* Add/Edit/Delete Dialogs */}
        {renderDialogs()}
      </Box>
    )
  }

  // Multi-contact display with edit capabilities
  function renderDialogs() {
    return (
      <>
        {/* Add/Edit Contact Dialog */}
        <Dialog
          open={isAddDialogOpen || isEditDialogOpen}
          onClose={() => {
            setIsAddDialogOpen(false)
            setIsEditDialogOpen(false)
            setEditingContact(null)
            resetForm()
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              {editingContact ? 'Update contact information.' : 'Add a new contact for this customer.'}
            </DialogContentText>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, py: 2 }}>
              <TextField
                id="first_name"
                label="First Name"
                size="small"
                fullWidth
                value={formData.first_name || ""}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="John"
              />
              <TextField
                id="last_name"
                label="Last Name"
                size="small"
                fullWidth
                value={formData.last_name || ""}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Doe"
              />
              <TextField
                id="email"
                label="Email"
                type="email"
                size="small"
                fullWidth
                value={formData.email || ""}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
              />
              <TextField
                id="email2"
                label="Email 2"
                type="email"
                size="small"
                fullWidth
                value={formData.email2 || ""}
                onChange={(e) => setFormData({ ...formData, email2: e.target.value })}
                placeholder="john.doe@personal.com"
              />
              <TextField
                id="phone"
                label="Phone"
                size="small"
                fullWidth
                value={formData.phone || ""}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
              <TextField
                id="phone2"
                label="Phone 2"
                size="small"
                fullWidth
                value={formData.phone2 || ""}
                onChange={(e) => setFormData({ ...formData, phone2: e.target.value })}
                placeholder="+1 (555) 987-6543"
              />
              <TextField
                id="mobile"
                label="Mobile"
                size="small"
                fullWidth
                value={formData.mobile || ""}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+1 (555) 987-6543"
              />
              <TextField
                id="company_name"
                label="Company"
                size="small"
                fullWidth
                value={formData.company_name || ""}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder="Company Name"
              />
              <TextField
                id="job_title"
                label="Job Title"
                size="small"
                fullWidth
                value={formData.job_title || ""}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                placeholder="CEO, CTO, etc."
              />
              <TextField
                id="website"
                label="Website"
                size="small"
                fullWidth
                value={formData.website || ""}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://company.com"
              />
              <TextField
                id="timezone"
                label="Timezone"
                size="small"
                fullWidth
                value={formData.timezone || ""}
                onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                placeholder="America/New_York"
              />
              <TextField
                id="address"
                label="Street Address"
                size="small"
                fullWidth
                sx={{ gridColumn: "span 2" }}
                value={formData.address || ""}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street"
              />
              <TextField
                id="city"
                label="City"
                size="small"
                fullWidth
                value={formData.city || ""}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="New York"
              />
              <TextField
                id="state"
                label="State/Province"
                size="small"
                fullWidth
                value={formData.state || ""}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="NY"
              />
              <TextField
                id="country"
                label="Country"
                size="small"
                fullWidth
                value={formData.country || ""}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="United States"
              />
              <TextField
                id="postal"
                label="Postal Code"
                size="small"
                fullWidth
                value={formData.postal_code || ""}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                placeholder="10001"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                setIsAddDialogOpen(false)
                setIsEditDialogOpen(false)
                setEditingContact(null)
                resetForm()
              }}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveContact}
              disabled={isSaving}
              {...(isSaving ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
            >
              {isSaving
                ? (editingContact ? "Updating..." : "Creating...")
                : (editingContact ? "Update Contact" : "Add Contact")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Contact Dialog */}
        <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Contact</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              Are you sure you want to delete this contact? This action cannot be undone.
            </DialogContentText>
            {contactToDelete && (
              <Box sx={{ py: 2 }}>
                <Box sx={{ borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft", p: 2 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                    {contactToDelete.display_name}
                  </Typography>
                  {contactToDelete.email && (
                    <Typography sx={{ fontSize: 14, color: "text.secondary" }}>{contactToDelete.email}</Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDeleteContact}
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : <Trash2 size={16} />}
            >
              {isDeleting ? "Deleting..." : "Delete Contact"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} {...(className ? { className } : {})}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Users size={16} />
          <Typography component="h4" sx={{ fontWeight: 500 }}>Contact Information</Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<Plus size={16} />}
          onClick={handleAddContact}
        >
          Add Contact
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {contacts.map((contact) => {
          const isExpanded = expandedContactIds.has(contact.id)

          return (
          <Box key={contact.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* Contact Header — the whole name cluster is the expand/collapse
                toggle; the "..." menu button next to it stops propagation so
                opening the menu never folds the card. */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {/* A real <button> (ButtonBase), not a role="button" div: the
                  browser then applies its own :focus-visible heuristic — ring
                  on keyboard focus, none on a mouse click — and Enter/Space
                  activate it natively. */}
              <ButtonBase
                disableRipple
                aria-expanded={isExpanded}
                aria-controls={`contact-details-${contact.id}`}
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${contact.display_name}`}
                onClick={() => toggleContactExpanded(contact.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  gap: 1.5,
                  flex: 1,
                  minWidth: 0,
                  borderRadius: 1,
                  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    color: "text.secondary",
                    flexShrink: 0,
                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: (t) => t.transitions.create("transform", { duration: t.transitions.duration.shortest }),
                  }}
                >
                  <ChevronDown size={16} />
                </Box>
                <Box sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: "primary.main", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                  {contact.initials}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{contact.display_name}</Typography>
                </Box>
              </ButtonBase>
              <IconButton size="small" sx={{ width: 32, height: 32, flexShrink: 0 }} aria-label="Open menu" onClick={(e) => { e.stopPropagation(); setMenuAnchor({ el: e.currentTarget, contactId: contact.id }) }}>
                <MoreHorizontal size={16} />
              </IconButton>
              <Menu
                anchorEl={menuAnchor?.contactId === contact.id ? menuAnchor.el : null}
                open={menuAnchor?.contactId === contact.id}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={() => { setMenuAnchor(null); handleEditContact(contact) }}>
                  <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                  Edit Contact
                </MenuItem>
                <MenuItem
                  onClick={() => { setMenuAnchor(null); handleDeleteContact(contact) }}
                  sx={{ color: "error.main" }}
                >
                  <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                  Delete Contact
                </MenuItem>
              </Menu>
            </Box>

            <Collapse in={isExpanded} unmountOnExit id={`contact-details-${contact.id}`}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Divider />

                {/* Customer Support Portal access — sits between the contact
                    header and the contact details so it reads as a primary
                    action rather than buried metadata. */}
                <ContactPortalAccessButton
                  contactId={contact.id}
                  state={portalStates[contact.id] ?? null}
                  onChanged={reloadPortalStates}
                />

                <Divider />

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1.5 }}>
                  {/* Job Title & Company */}
                  {contact.job_title && (
                    <InfoRow
                      icon={<Building2 size={16} />}
                      label="Job Title"
                      value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.job_title}</Typography>}
                    />
                  )}

                  {/* Email */}
                  <InfoRow
                    icon={<Mail size={16} />}
                    label="Email"
                    value={
                      <Typography sx={{ fontSize: 12, color: contact.email ? "text.secondary" : "text.disabled", ...(contact.email ? {} : { fontStyle: "italic" }) }}>
                        {contact.email || 'Email not specified'}
                      </Typography>
                    }
                  />

                  {/* Email 2 - Only show if primary email exists */}
                  {contact.email2 && (
                    <InfoRow icon={<Mail size={16} />} label="Email 2" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.email2}</Typography>} />
                  )}

                  {/* Phone */}
                  {contact.phone && contact.phone.trim() ? (
                    <InfoRow icon={<Phone size={16} />} label="Phone" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.phone}</Typography>} />
                  ) : null}

                  {/* Phone 2 */}
                  {contact.phone2 && contact.phone2.trim() ? (
                    <InfoRow icon={<Phone size={16} />} label="Phone 2" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.phone2}</Typography>} />
                  ) : null}

                  {/* Mobile */}
                  {contact.mobile && contact.mobile.trim() ? (
                    <InfoRow icon={<Phone size={16} />} label="Mobile" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.mobile}</Typography>} />
                  ) : null}

                  {/* Show message if no phone numbers provided */}
                  {!contact.phone?.trim() && !contact.phone2?.trim() && !contact.mobile?.trim() && (
                    <InfoRow
                      icon={<Phone size={16} />}
                      label="Phone"
                      value={<Typography sx={{ fontSize: 12, color: "text.disabled", fontStyle: "italic" }}>Phone not specified</Typography>}
                    />
                  )}

                  {/* Website */}
                  {contact.website && (
                    <InfoRow icon={<Globe size={16} />} label="Website" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.website}</Typography>} />
                  )}

                  {/* Location */}
                  {contact.location && (
                    <InfoRow icon={<MapPin size={16} />} label="Location" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.location}</Typography>} />
                  )}

                  {/* Full Address (if available) */}
                  {!contact.location && (
                    (contact.address?.trim() || contact.city?.trim() || contact.state?.trim() || contact.country?.trim()) ? (
                      <InfoRow
                        icon={<MapPin size={16} />}
                        label="Address"
                        alignTop
                        value={
                          <Box sx={{ fontSize: 12, color: "text.secondary", display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {contact.address && <Typography sx={{ fontSize: 12 }}>{contact.address}</Typography>}
                            {(contact.city || contact.state || contact.postal_code) && (
                              <Typography sx={{ fontSize: 12 }}>
                                {[contact.city, contact.state].filter(v => v && v.trim()).join(', ')}
                                {contact.postal_code && contact.postal_code.trim() && ` ${contact.postal_code.trim()}`}
                              </Typography>
                            )}
                            {contact.country && <Typography sx={{ fontSize: 12 }}>{contact.country}</Typography>}
                            {!contact.address && !contact.city && !contact.state && !contact.postal_code && !contact.country && (
                              <Typography sx={{ fontSize: 12, color: "text.disabled", fontStyle: "italic" }}>No address information</Typography>
                            )}
                          </Box>
                        }
                      />
                    ) : (
                      <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 2, fontSize: 14, color: "text.secondary" }}>
                        Address not provided
                      </Box>
                    )
                  )}

                  {/* Timezone */}
                  {contact.timezone && (
                    <InfoRow icon={<Clock size={16} />} label="Timezone" value={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>{contact.timezone}</Typography>} />
                  )}
                </Box>
              </Box>
            </Collapse>
          </Box>
          )
        })}

        {contacts.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Box sx={{ display: "flex", justifyContent: "center", color: "text.disabled", mb: 1 }}><Users size={32} /></Box>
            <Typography sx={{ fontSize: 14 }}>No contacts added yet</Typography>
            <Typography sx={{ fontSize: 12 }}>Click "Add Contact" to get started</Typography>
          </Box>
        )}
      </Box>

      {renderDialogs()}
    </Box>
  )
}
